import type {} from '@mui/material/themeCssVarsAugmentation';
import { createTheme, responsiveFontSizes } from '@mui/material/styles';
import darkScrollbar from '@mui/material/darkScrollbar';
import {
  PRIMARY, PRIMARY_DARK,
  SECONDARY, SECONDARY_LIGHT, SECONDARY_DARK,
  SUCCESS, SUCCESS_DARK,
  INFO, INFO_DARK,
  ERROR, ERROR_LIGHT,
  WARNING,
} from '@/constants/colors';

const sharedPalette = {
  primary:   { main: PRIMARY,   dark: PRIMARY_DARK },
  secondary: { main: SECONDARY, light: SECONDARY_LIGHT, dark: SECONDARY_DARK },
  success:   { main: SUCCESS,   dark: SUCCESS_DARK },
  info:      { main: INFO,      dark: INFO_DARK },
  error:     { main: ERROR,     light: ERROR_LIGHT },
  warning:   { main: WARNING },
};

export const appTheme = responsiveFontSizes(createTheme({
  cssVariables: {
    colorSchemeSelector: 'class',
  },
  colorSchemes: {
    light: {
      palette: {
        ...sharedPalette,
        text: {
          secondary: 'rgba(0, 0, 0, 0.87)',
        },
        action: {
          active: 'rgba(0, 0, 0, 0.87)',
        },
        background: {
          default: 'rgb(250, 250, 250)',
          paper: '#ffffff',
        },
      },
    },
    dark: {
      palette: {
        ...sharedPalette,
        background: {
          default: '#121212',
          paper: '#1e1e1e',
        },
      },
    },
  },
  shape: {
    borderRadius: 8,
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
    fontSize: 16,
    body1: { fontSize: '16px', lineHeight: 1.5, fontWeight: 400 },
    body2: { fontSize: '14px', lineHeight: 1.43, fontWeight: 400 },
    h2:    { fontSize: '20px', lineHeight: 1.6,  fontWeight: 600 },
    h4:    { fontSize: '24px', lineHeight: 1.33, fontWeight: 600 },
    h6:    { fontSize: '18px', lineHeight: 1.5,  fontWeight: 400 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: (theme) => ({
        'html, body, #root': {
          height: '100%',
          minHeight: '100dvh',
        },
        body: {
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          ...theme.applyStyles('dark', darkScrollbar()),
        },
        '.scrollbar-custom': {
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(128, 128, 128, 0.3) transparent',
          '&::-webkit-scrollbar': { width: 8 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(128, 128, 128, 0.3)',
            borderRadius: 4,
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: 'rgba(128, 128, 128, 0.5)',
          },
        },
        '.scrollbar-hide': {
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        },
        '.map-viewport-lock': {
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100dvh',
          overflow: 'hidden',
        },
      }),
    },
    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: '8px' },
        sizeSmall: {
          padding: '4px',
          fontSize: '18px',
          '& .MuiSvgIcon-root': { fontSize: '18px' },
        },
      },
      defaultProps: {
        size: 'small' as const,
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none' as const, borderRadius: '8px' },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 3 },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
  },
}));
